/**
 * Asset service client — typed API calls for hardware assets, classification views,
 * refresh forecast, and Meraki subnet management.
 */

import { apiClient } from '@/services/apiClient';
import type {
  HardwareAsset,
  MerakiSubnetEntry,
  PaginatedResponse,
  AssetLifecycleStatus,
  DeviceToEmployeeRatio,
  RefreshForecastResult,
} from '@/types';

// ─── Asset Types ───────────────────────────────────────────────────────

export interface AssetListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  siteNumber?: string;
  lifecycleStatus?: string;
  deviceType?: string;
  deviceRole?: string;
  manufacturer?: string;
  model?: string;
  assignedUser?: string;
  minAge?: number;
  maxAge?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateAssetInput {
  serialNumber: string;
  deviceName?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  lifecycleStatus?: string;
  siteNumber?: string | null;
  deploymentDate?: string | null;
  acquisitionDate?: string | null;
  costBasis?: number | null;
  assignedUser?: string | null;
  lastLoggedInUser?: string | null;
  lastContactDate?: string | null;
  deviceType?: string | null;
  deviceRole?: string | null;
  os?: string | null;
  ipAddresses?: string | null;
  locationSource?: string | null;
  notes?: string | null;
}

export type UpdateAssetInput = Partial<CreateAssetInput> & { updatedAt: string };

export interface RedeploymentResult {
  candidates: HardwareAsset[];
  highPriority: HardwareAsset[];
}

export interface BulkImportResult {
  imported: number;
  updated: number;
  errors: Array<{ row: number; serialNumber: string; error: string }>;
}

export interface SubnetBulkImportResult {
  imported: number;
  updated: number;
  errors: Array<{ row: number; error: string }>;
}

export interface SubnetListParams {
  siteNumber?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateSubnetInput {
  subnetRange: string;
  siteNumber: string;
  networkName?: string | null;
  vlanId?: string | null;
}

// ─── Service ───────────────────────────────────────────────────────────

export const assetService = {
  // Asset CRUD
  list(params?: AssetListParams): Promise<PaginatedResponse<HardwareAsset>> {
    return apiClient.get('assets', params as Record<string, string | number | boolean | undefined>);
  },

  // POST-based search (reliable through SWA proxy)
  search(params: AssetListParams): Promise<PaginatedResponse<HardwareAsset>> {
    return apiClient.post('assets/search', params);
  },

  getById(assetId: number): Promise<HardwareAsset> {
    return apiClient.get(`assets/${assetId}`);
  },

  create(input: CreateAssetInput): Promise<HardwareAsset> {
    return apiClient.post('assets', input);
  },

  update(assetId: number, input: UpdateAssetInput): Promise<HardwareAsset> {
    return apiClient.put(`assets/${assetId}`, input);
  },

  delete(assetId: number): Promise<void> {
    return apiClient.delete(`assets/${assetId}`);
  },

  transitionLifecycle(assetId: number, targetStatus: AssetLifecycleStatus): Promise<HardwareAsset> {
    return apiClient.put(`assets/${assetId}/lifecycle`, { targetStatus });
  },

  bulkImport(assets: CreateAssetInput[]): Promise<BulkImportResult> {
    return apiClient.post('assets/import', { assets });
  },

  // Classification views
  getRedeploymentCandidates(): Promise<RedeploymentResult> {
    return apiClient.get('assets/redeployment');
  },

  getGhostAssets(): Promise<HardwareAsset[]> {
    return apiClient.get('assets/ghost');
  },

  getMislocatedDevices(): Promise<Array<HardwareAsset & { ipResolvedSite: string | null }>> {
    return apiClient.get('assets/mislocated');
  },

  getStaleDevices(): Promise<HardwareAsset[]> {
    return apiClient.get('assets/stale');
  },

  // Refresh forecast
  getRefreshForecast(): Promise<RefreshForecastResult> {
    return apiClient.get('assets/refresh-forecast');
  },

  // Device-to-employee ratio
  getDeviceToEmployeeRatio(siteNumber: string): Promise<DeviceToEmployeeRatio> {
    return apiClient.get(`assets/device-ratio/${siteNumber}`);
  },

  // Meraki subnet management
  listSubnets(params?: SubnetListParams): Promise<PaginatedResponse<MerakiSubnetEntry>> {
    return apiClient.get('assets/meraki-subnet', params as Record<string, string | number | boolean | undefined>);
  },

  getSubnet(subnetId: number): Promise<MerakiSubnetEntry> {
    return apiClient.get(`assets/meraki-subnet/${subnetId}`);
  },

  createSubnet(input: CreateSubnetInput): Promise<MerakiSubnetEntry> {
    return apiClient.post('assets/meraki-subnet', input);
  },

  updateSubnet(subnetId: number, input: Partial<CreateSubnetInput>): Promise<MerakiSubnetEntry> {
    return apiClient.put(`assets/meraki-subnet/${subnetId}`, input);
  },

  deleteSubnet(subnetId: number): Promise<void> {
    return apiClient.delete(`assets/meraki-subnet/${subnetId}`);
  },

  bulkImportSubnets(subnets: CreateSubnetInput[]): Promise<SubnetBulkImportResult> {
    return apiClient.post('assets/meraki-subnet/import', { subnets });
  },

  resolveIP(ip: string): Promise<{ ip: string; siteNumber: string | null }> {
    return apiClient.get(`assets/resolve-ip/${ip}`);
  },
};
